import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

import i18n from "i18n";

import { findOption } from "utils";
import { cx, cva } from "class-variance-authority";

import { Button as B } from "../ui/button";
import { Input as I } from "../ui/input";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { ComboBoxResponsive } from "./Combobox";
import { useState } from "react";

export const Button = B;
export const Input = I;

export const SelectGray = ({
  options = [],
  value,
  onChange,
  placeholder,
  isDisabled,
  isLoading,
  isSearchable = true,
  isMulti,
  isClearable,
  defaultValue,
  namespace,
  groups,
}: {
  options: Array<{ label: string; value: any }> | Array<string> | undefined;
  value?: string | string[] | any;
  onChange?: (e: any) => void;
  placeholder?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  isSearchable?: boolean;
  isMulti?: boolean;
  isClearable?: boolean;
  defaultValue?: string | string[] | any;
  namespace?: string;
  groups?: Array<{
    label?: string;
    options:
      | Array<{ label: string; value: any; disabled?: boolean }>
      | Array<string>;
  }>;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const { t } = useTranslation();

  const selectedValues = isMulti
    ? Array.isArray(value)
      ? value.map((v: any) => (typeof v === "object" && v?.value ? v.value : v))
      : []
    : value
      ? [typeof value === "object" && value?.value ? value.value : value]
      : [];

  const normalizedGroups = groups?.map((group) => ({
    label: group.label,
    options: convertOptions(group.options as any, t, namespace),
  }));

  const labelValueOptions = normalizedGroups
    ? normalizedGroups.flatMap((group) => group.options)
    : convertOptions(options as any, t, namespace);

  const handleSelect = (currentValue: string, label: string) => {
    if (isMulti) {
      const newValues = selectedValues.includes(currentValue)
        ? selectedValues.filter((v: string) => v !== currentValue)
        : [...selectedValues, currentValue];
      onChange?.(newValues.map((v: string) => ({ value: v, label })));
    } else {
      onChange?.({ value: currentValue, label });
      setOpen(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (Array.isArray(selectedValues)) {
      onChange?.(null);
    } else {
      onChange?.([]);
    }
    setQuery("");
    setOpen(false);
  };

  const currentLabel = isMulti
    ? selectedValues
        .map((v: string) => {
          const opt = labelValueOptions.find((o) => o.value === v);
          return opt ? opt.label : v;
        })
        .join(", ")
    : (() => {
        const val = selectedValues[0];
        const opt = labelValueOptions.find((o) => o.value === val);
        return opt ? opt.label : val;
      })();

  return (
    <ComboBoxResponsive
      open={open}
      onOpenChange={setOpen}
      contentClassName="w-full p-0"
      trigger={
        <button
          type="button"
          disabled={isDisabled || isLoading}
          aria-expanded={open}
          className={cx(
            "flex h-10 w-full cursor-pointer items-center justify-between border-b border-slate-200 bg-white px-3 text-sm text-slate-700 transition-colors hover:border-slate-300",
            !selectedValues.length && "text-muted-foreground",
          )}
        >
          {selectedValues.length ? (
            <span className="truncate">{currentLabel}</span>
          ) : (
            <span className="text-[#677e7e] font-thin">
              {placeholder || t("Select an Option")}
            </span>
          )}

          {isClearable && (value?.length || selectedValues?.length) ? (
            <X
              className="ml-2 h-4 w-4 shrink-0 opacity-50"
              onClick={handleClear}
            />
          ) : (
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          )}
        </button>
      }
    >
      <Command
        filter={(value, search) => {
          if (value.toLowerCase().includes(search.toLowerCase())) return 1;
          return 0;
        }}
      >
        {isSearchable && (
          <CommandInput
            placeholder={t("Search...")}
            value={query}
            onValueChange={setQuery}
          />
        )}
        <CommandList>
          <CommandEmpty>{t("No results found.")}</CommandEmpty>
          {normalizedGroups ? (
            normalizedGroups.map((group, groupIndex) => (
              <CommandGroup
                key={group.label ?? groupIndex}
                heading={group.label}
              >
                {group.options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    disabled={(option as any).disabled}
                    onSelect={(currentValue) => {
                      const matchedOption = labelValueOptions.find(
                        (opt) => opt.label === currentValue,
                      );
                      if ((matchedOption as any)?.disabled) return;
                      handleSelect(
                        matchedOption ? matchedOption.value : currentValue,
                        matchedOption?.label,
                      );
                    }}
                  >
                    <Check
                      className={cx(
                        "mr-2 h-4 w-4",
                        selectedValues.includes(option.value)
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))
          ) : (
            <CommandGroup>
              {labelValueOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={(currentValue) => {
                    const matchedOption = labelValueOptions.find(
                      (opt) => opt.label === currentValue,
                    );
                    handleSelect(
                      matchedOption ? matchedOption.value : currentValue,
                      matchedOption?.label,
                    );
                  }}
                >
                  <Check
                    className={cx(
                      "mr-2 h-4 w-4",
                      selectedValues.includes(option.value)
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </Command>
    </ComboBoxResponsive>
  );
};

const convertOptions = (
  options: Array<{ label: string; value: string }> | Array<string | any>,
  t: any,
  namespace?: string,
) => {
  if (!options || options.length === 0) return [];

  // If the first element is an object with label/value, assume all are
  if (
    typeof options[0] === "object" &&
    "label" in options[0] &&
    "value" in options[0]
  ) {
    return options as Array<{ label: string; value: string }>;
  }

  // Otherwise treat as primitives and map to label/value
  return (options as Array<string | any>).map((option) => ({
    label: namespace ? t(`${namespace}:${option}`) : t(String(option)),
    value: option,
  }));
};

export const ControlledInput = ({
  placeholder = i18n.t("Input an option"),
  renderProps,
  disabled,
  type = "text",
  inputMode,
  pattern,
}: {
  disabled?: boolean;
  placeholder?: string;
  renderProps: any;
  type?: string;
  inputMode?: "decimal" | "numeric" | "tel" | "url";
  pattern?: string;
}) => {
  const { t } = useTranslation();

  const hasError = renderProps.formState?.errors[renderProps.field.name];
  const error = () => {
    if (hasError?.type === "required") {
      return (
        <small className="-mt-3 text-destructive">
          {t("Input cannot be empty")}
        </small>
      );
    } else if (hasError) {
      return (
        <small className="-mt-3 text-destructive">
          {hasError.message ? hasError.message : t("Input is not valid")}
        </small>
      );
    }
  };
  return (
    <>
      <Input
        inputMode={inputMode}
        pattern={pattern}
        type={type}
        step=".0001"
        placeholder={placeholder}
        disabled={disabled}
        className={`${hasError ? "border border-red-dark200" : ""}`}
        {...renderProps.field}
        onBlur={(blur: any) => {
          // renderProps.field.onBlur();
          renderProps.field.onChange(renderProps.field.value?.trim());
        }}
      />
      {error()}
    </>
  );
};

export const ControlledSelectGray = ({
  options = [],
  renderProps,
  isLoading,
  isDisabled,
  isMulti = false,
  isClearable,
  namespace,
  handleChange,
  isSearchable = true,
  defaultValue,
  filterOption,
  placeholder = i18n.t("Select an Option"),
}: {
  isSearchable?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  isClearable?: boolean;
  renderProps: any;
  placeholder?: string;
  isMulti?: boolean;
  handleChange?: any;
  namespace?: string;
  defaultValue?: string;
  options?: Array<{ label: string; value: string }> | Array<string>;
  filterOption?: any;
}) => {
  const { t } = useTranslation();

  const isRequired =
    renderProps?.fieldState?.error &&
    renderProps?.formState?.errors[renderProps?.field?.name]?.type ===
      "required";

  return (
    <div>
      <SelectGray
        options={options}
        value={renderProps.field.value}
        onChange={(e: any) => {
          if (handleChange) {
            handleChange(e);
          }

          const value = isMulti
            ? e.map((opt: any) => opt.value)
            : (e?.value ?? "");

          renderProps.field.onChange(value);
        }}
        placeholder={placeholder}
        isDisabled={isDisabled || isLoading}
        isLoading={isLoading}
        isSearchable={isSearchable}
        isMulti={isMulti}
        namespace={namespace}
        isClearable={isClearable}
      />

      {isRequired && (
        <small className="text-destructive">{t("Input cannot be empty")}</small>
      )}
    </div>
  );
};

export const ControlledCreatableSelectGray = ({
  options = [],
  renderProps,
  required,
  isLoading,
  isDisabled,
  isClearable,
  formatCreateLabel,
  placeholder = i18n.t("Select or add an option"),
}: {
  isDisabled?: boolean;
  isLoading?: boolean;
  required?: boolean;
  isClearable?: boolean;
  renderProps: any;
  placeholder?: string;
  formatCreateLabel?: any;
  options?: Array<{ label: string; value: string }> | Array<string>;
}) => {
  const { t } = useTranslation();
  const labelValueOptions = convertOptions(options, t);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const value = renderProps.field.value;
  const selectedOption = findOption(value, labelValueOptions);

  const handleSelect = (currentValue: string) => {
    renderProps.field.onChange(currentValue);
    setOpen(false);
    setQuery("");
  };

  const handleCreate = () => {
    if (query.trim()) {
      const exactMatch = labelValueOptions.find(
        (opt) => opt.label.toLowerCase() === query.toLowerCase().trim(),
      );

      if (exactMatch) {
        handleSelect(exactMatch.value);
      } else {
        handleSelect(query.trim());
      }
    }
  };

  const currentLabel = selectedOption ? selectedOption.label : value;

  return (
    <ComboBoxResponsive
      open={open}
      onOpenChange={setOpen}
      contentClassName="w-full p-0"
      trigger={
        <button
          disabled={isDisabled || isLoading}
          aria-expanded={open}
          className={cx(
            "flex h-11 w-full items-center justify-between rounded-md border-0 bg-[#e8eeed4d] px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
            !value && "text-muted-foreground",
          )}
        >
          {value ? (
            <span className="truncate">{currentLabel}</span>
          ) : (
            <span className="text-[#677e7e]">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      }
    >
      <Command
        filter={(value, search) => {
          if (value.toLowerCase().includes(search.toLowerCase())) return 1;
          return 0;
        }}
      >
        <CommandInput
          placeholder={t("Search or create...")}
          value={query}
          onValueChange={setQuery}
          onBlur={handleCreate}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const visibleOptions = labelValueOptions.filter((opt) =>
                opt.label.toLowerCase().includes(query.toLowerCase()),
              );

              if (visibleOptions.length === 0 && query.trim()) {
                e.preventDefault();
                handleCreate();
              }
            }
          }}
        />
        <CommandList>
          <CommandEmpty className="py-2 px-2 text-sm text-center">
            {query ? (
              <div
                className="cursor-pointer p-2 hover:bg-slate-100 rounded-sm text-left"
                onMouseDown={(e) => {
                  // Use onMouseDown to prevent blur from firing first
                  e.preventDefault();
                  handleSelect(query.trim());
                }}
              >
                {formatCreateLabel
                  ? formatCreateLabel(query)
                  : `${t("Create")} "${query}"`}
              </div>
            ) : (
              t("No results found.")
            )}
          </CommandEmpty>
          <CommandGroup>
            {labelValueOptions.map((option) => (
              <CommandItem
                key={option.value}
                value={option.label} // Use label for filtering
                // Prevent input blur (which triggers handleCreate) before onSelect fires
                onMouseDown={(e) => e.preventDefault()}
                onSelect={(currentValue) => {
                  // currentValue here comes from cmdk which is lowercased value prop (label in our case)
                  // We need to map it back to the option value
                  const matchedOption = labelValueOptions.find(
                    (opt) =>
                      opt.label.toLowerCase() === currentValue.toLowerCase(),
                  );
                  handleSelect(
                    matchedOption ? matchedOption.value : currentValue,
                  );
                }}
              >
                <Check
                  className={cx(
                    "mr-2 h-4 w-4",
                    value === option.value ? "opacity-100" : "opacity-0",
                  )}
                />
                {option.label}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </ComboBoxResponsive>
  );
};

export const flexCol = cva(
  "flex flex-row items-center items-baseline flex-row xl:flex-col justify-between border-s-2 ps-2 text-sm",
  {
    variants: {
      intent: {
        muted: "border-gray-light200",
        green: "border-[#009683]",
        none: "border-transparent",
      },
    },
  },
);

export const flexH = cva("flex flex-col xl:flex-row gap-2 xl:gap-10");
